import { bottom, top } from "@popperjs/core";
import DeleteIcon from "@shared/svg/delete-icon.svg?react";
import type { Product } from "@shared/types/product";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_VARIANTS } from "@shared/ui/Button";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import { CheckboxEmpty, CheckboxFilled } from "@shared/ui/Inputs";
import Overlay from "@shared/ui/Overlay";
import { DotsThree as ThreeDots, Eye, MinusCircle } from "phosphor-react";
import { useRef, useState } from "react";
import { useIntl } from "react-intl";
import useOnClickOutside from "use-onclickoutside";

type AssetTemplateListItemPropsType = {
  template: Product;
  thumbnail?: string;
  onImageLoadError: React.ReactEventHandler<HTMLImageElement>;
  templateLabelComponent?: React.ReactNode;
  showDeleteButton?: boolean;
  tooltipText?: string;
  isSharedPage?: boolean;
  onButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  customImageComponent?: () => React.ReactElement;
  onClickUnAssign?: (_: string) => void;
  onClickView?: (_: string) => void;
  isLastItem?: boolean;
  margin?: string;
  showUnassignButton?: boolean;
  /**
   * When true, the leading thumbnail is replaced by a selection checkbox on
   * hover (and permanently once selected or any item is selected).
   */
  selectable?: boolean;
  isSelected?: boolean;
  anySelected?: boolean;
  onToggleSelect?: () => void;
};

const AssetTemplateListItem = ({
  template,
  thumbnail = "",
  showUnassignButton = false,
  isSharedPage = false,
  onImageLoadError,
  showDeleteButton = true,
  onClickUnAssign = () => {},
  onClickView = () => {},
  tooltipText = "",
  onButtonClick = () => {},
  isLastItem = false,
  margin = "mb-lg",
  customImageComponent = null,
  selectable = false,
  isSelected = false,
  anySelected = false,
  onToggleSelect,
}: AssetTemplateListItemPropsType) => {
  const { messages } = useIntl();
  const [showActionPopup, setShowActionPopup] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const ref = useRef(null);
  const targetRef = useRef(null);

  useOnClickOutside(ref, () => setShowActionPopup(false));

  const showCheckbox =
    selectable && (isHovered || isSelected || anySelected);

  return (
    <div
      className={`group h-6xl px-md ${margin} rounded-lg border border-solid border-primary bg-primary hover:bg-accent flex items-center justify-between`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center w-3/4 min-w-0">
        {showCheckbox ? (
          <div
            className="flex items-center justify-center flex-shrink-0 cursor-pointer mr-md"
            style={{ width: 48, height: 36 }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onToggleSelect?.();
            }}
          >
            {isSelected ? (
              <CheckboxFilled width={18} height={18} className="flex-shrink-0" />
            ) : (
              <span
                className="relative flex items-center justify-center"
                style={{ width: 18, height: 18 }}
              >
                <span
                  className="absolute rounded-[4px] bg-white"
                  style={{ inset: 2 }}
                />
                <CheckboxEmpty
                  width={18}
                  height={18}
                  className="relative flex-shrink-0"
                />
              </span>
            )}
          </div>
        ) : customImageComponent ? (
          customImageComponent()
        ) : (
          thumbnail && (
            <figure className="flex items-center justify-center p-0 overflow-hidden rounded-md my-0 mr-md h-3xl w-4xl flex-shrink-0">
              <img
                src={thumbnail}
                alt={template.title}
                onError={onImageLoadError}
              />
            </figure>
          )
        )}
        <div className="flex max-w-[200px] md:max-w-[500px] min-w-0">
          <div className="inline-flex items-center truncate">
            <Headline
              size={HEADLINE_SIZES.X_SMALL}
              className="truncate"
              title={template.title}
            >
              {template.title}
            </Headline>
          </div>
        </div>
      </div>
      <div className="flex justify-between w-1/4">
        <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="truncate">
          <span className="text-secondary truncate">
            {messages?.["hierarchy"]?.["assetTemplates"]?.["model"] || "Model"}
            :&nbsp;
          </span>
          <span className="max-w-20 truncate">{template.templateId}</span>
        </BodyText>

        {isSharedPage ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowActionPopup(true);
            }}
            ref={targetRef}
            className="flex"
          >
            {/* @ts-ignore */}
            <ThreeDots color="text-primary" size={20} />
            <Overlay
              arrowClasses="before:bg-primary before:shadow-alert"
              className="border border-solid border-primary bg-primary max-w-44xl rounded shadow-alert w-full"
              containerRef={document.body}
              offset={[0, 10]}
              placement={isLastItem ? top : bottom}
              showOverlay={showActionPopup}
              targetRef={targetRef}
            >
              <div className="bg-white rounded-lg w-full py-md" ref={ref}>
                <div className="flex flex-col">
                  <span
                    className="capitalize cursor-pointer font-medium hover:bg-gray-5 px-4 py-2 text-gray-1000 text-xs"
                    onClick={() => {
                      onClickView(template._id);
                    }}
                  >
                    <div className="flex items-center cursor-pointer">
                      <div className="text-secondary mr-sm w-lg h-lg">
                        <Eye size={16} />
                      </div>
                      <BodyText size={BODY_TEXT_SIZES.X_SMALL}>
                        {messages.common?.["view"]}
                      </BodyText>
                    </div>
                  </span>
                  {(!isSharedPage || showUnassignButton) && (
                    <span
                      className="capitalize cursor-pointer font-medium hover:bg-gray-5 px-4 py-2 text-gray-1000 text-xs"
                      onClick={() => {
                        onClickUnAssign(template._id);
                      }}
                    >
                      <div className="flex items-center cursor-pointer">
                        <div className="text-secondary mr-sm w-lg h-lg">
                          <MinusCircle size={16} />
                        </div>
                        <BodyText size={BODY_TEXT_SIZES.X_SMALL}>
                          {messages.emails?.["unassign"]}
                        </BodyText>
                      </div>
                    </span>
                  )}
                </div>
              </div>
            </Overlay>
          </div>
        ) : (
          showDeleteButton && (
            <Button
              variant={BUTTON_VARIANTS.LINK}
              leadingIcon={
                <DeleteIcon className="text-secondary" width={20} height={20} />
              }
              tooltipText={tooltipText}
              onClick={onButtonClick}
            />
          )
        )}
      </div>
    </div>
  );
};

export default AssetTemplateListItem;
